import React from 'react';
import { FaArrowRight } from 'react-icons/fa';
import { useFetchWithCache } from '../hooks/useFetchWithCache';
import LoadingSpinner from '../components/LoadingSpinner';
import { Eyebrow, SectionTitle, SectionLead, GoldDivider, PrimaryButton } from './styles/Shared.styles';
import {
  Hero, HeroBg, HeroOverlay, HeroInner, HeroContent, HeroButtons, HeroStat,
  IntroSection, IntroGrid, IntroImage, IntroText,
  CompaniesBand, CompanyGrid, CompanyCard,
  ServicesBand, ServicesInner, ServiceGrid, ServicePill,
  ContactCta,
} from './styles/Home.styles';

const Home = () => {
  const { data: home, loading } = useFetchWithCache('/home');
  const { data: companies } = useFetchWithCache('/companies');
  const { data: about } = useFetchWithCache('/about');

  if (loading && !home) return <LoadingSpinner fullPage />;
  const h = home || {};

  const visibleCompanies = Array.isArray(companies)
    ? [...companies]
        .filter(c => c && (c.isPublished === undefined || c.isPublished))
        .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
    : [];

  const servicesSection = Array.isArray(about?.sections)
    ? about.sections.find(s => s.key === 'services')
    : null;
  const services = servicesSection?.items || [];

  return (
    <>
      <Hero>
        <HeroBg $src={h.heroImage} />
        <HeroOverlay />
        <HeroInner>
          <HeroContent>
            <div className="eyebrow">Ehcerex Group of Companies</div>
            <h1>
              {h.heroTitle ? (
                <>{h.heroTitle.split(' ').slice(0, -1).join(' ')} <em>{h.heroTitle.split(' ').slice(-1)}</em></>
              ) : (
                <>Building the future across <em>industries</em>.</>
              )}
            </h1>
            <p>{h.heroDescription || 'A diversified portfolio of companies delivering excellence in manufacturing, finance, telecommunications, hospitality, agriculture, education and more.'}</p>
            <HeroButtons>
              <PrimaryButton href="/about">Discover Ehcerex <FaArrowRight /></PrimaryButton>
              <PrimaryButton
                href="/contact"
                style={{ background: 'transparent', color: '#fff', borderColor: '#C8A951' }}
              >
                Get In Touch
              </PrimaryButton>
            </HeroButtons>
          </HeroContent>

          <HeroStat>
            <div className="row">
              <div className="item">
                <div className="num">{visibleCompanies.length || '3+'}</div>
                <div className="label">Companies</div>
              </div>
              <div className="item">
                <div className="num">{services.length || '13'}</div>
                <div className="label">Industries</div>
              </div>
              <div className="item">
                <div className="num">100%</div>
                <div className="label">Ownership</div>
              </div>
              <div className="item">
                <div className="num">1</div>
                <div className="label">Vision</div>
              </div>
            </div>
          </HeroStat>
        </HeroInner>
      </Hero>

      {/* Intro */}
      <IntroSection>
        <IntroGrid>
          <IntroImage>
            {h.bannerImage ? (
              <img src={h.bannerImage} alt={h.title || 'Ehcerex'} />
            ) : (
              <div className="placeholder">E</div>
            )}
          </IntroImage>
          <IntroText>
            <Eyebrow>About Ehcerex</Eyebrow>
            <SectionTitle>{h.title || <>Welcome to <span>Ehcerex</span></>}</SectionTitle>
            <GoldDivider />
            <p className="lead">
              A diversified group of companies with a single vision: to deliver excellence across every industry we serve.
            </p>
            <p>
              {h.description || 'Ehcerex operates across manufacturing, financial services, telecommunications, hospitality, aviation, agriculture, education, health and more. Each of our companies carries the same commitment to quality, integrity and lasting impact.'}
            </p>
            <div style={{ marginTop: '2rem' }}>
              <PrimaryButton href="/about">Learn More <FaArrowRight /></PrimaryButton>
            </div>
          </IntroText>
        </IntroGrid>
      </IntroSection>

      {/* Companies */}
      {visibleCompanies.length > 0 && (
        <CompaniesBand>
          <div style={{ maxWidth: 1180, margin: '0 auto', textAlign: 'center' }}>
            <Eyebrow style={{ paddingLeft: 0 }}>Our Portfolio</Eyebrow>
            <SectionTitle>Our <span>Companies</span></SectionTitle>
            <GoldDivider $center />
            <SectionLead style={{ margin: '0 auto 1rem' }}>
              Explore the businesses that make up the Ehcerex group.
            </SectionLead>
          </div>
          <CompanyGrid>
            {visibleCompanies.map((c, i) => (
              <CompanyCard key={c.id || c.slug} to={`/companies/${c.slug}`}>
                <div className="num">/ {String(i + 1).padStart(2, '0')}</div>
                {c.logo && (
                  <div className="logo">
                    <img src={c.logo} alt={`${c.title} logo`} />
                  </div>
                )}
                <h3>{c.title}</h3>
                <p>{c.description ? c.description.slice(0, 140) + (c.description.length > 140 ? '...' : '') : 'Discover the work of this Ehcerex company.'}</p>
                <span className="more">View Company <FaArrowRight /></span>
              </CompanyCard>
            ))}
          </CompanyGrid>
        </CompaniesBand>
      )}

      {/* Services / Industries */}
      {services.length > 0 && (
        <ServicesBand>
          <ServicesInner>
            <div style={{ display: 'inline-block' }}>
              <div style={{
                fontSize: '0.78rem', letterSpacing: '0.3em', textTransform: 'uppercase',
                color: '#E6C96A', marginBottom: '0.75rem'
              }}>
                What We Do
              </div>
            </div>
            <h2>Industries We <em style={{ color: '#E6C96A', fontStyle: 'italic' }}>Serve</em></h2>
            <p className="sub">The sectors where Ehcerex is building, operating and growing.</p>
            <ServiceGrid>
              {services.map((s, i) => (
                <ServicePill key={i}>
                  <div className="dot">{String(i + 1).padStart(2, '0')}</div>
                  <span>{s}</span>
                </ServicePill>
              ))}
            </ServiceGrid>
          </ServicesInner>
        </ServicesBand>
      )}

      {/* Contact CTA */}
      <ContactCta>
        <Eyebrow style={{ paddingLeft: 0 }}>Let's Connect</Eyebrow>
        <h2>Ready to work with <span style={{ fontStyle: 'italic', color: '#9C8033' }}>Ehcerex</span>?</h2>
        <GoldDivider $center />
        <p>Whether you're interested in partnership, careers or learning more about our companies, we'd love to hear from you.</p>
        <PrimaryButton href="/contact">Contact Us <FaArrowRight /></PrimaryButton>
      </ContactCta>
    </>
  );
};

export default Home;
